import React from "react";
import { getSidebarMenuConfig } from "./config/menu.config";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import type { RootState } from "@store/store";
import sidebarLogo from "@assets/login-signup-logo.svg";
import { OptimizedSvg } from "../common/OptimizedSvg";
import { openProfileCompletionModal } from "@store/slices/modalSlice";
import { JOBS_MENU_KEY } from "./config/menu.keys";
import "./Sidebar.css";

export const Sidebar: React.FC = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const { user } = useSelector((state: RootState) => state.user);
	const menu = getSidebarMenuConfig(t);

	const isProfileComplete = user?.isProfileComplete ?? false;

	const handleNavigation = (route: string, menuKey: string) => {
		if (!isProfileComplete && menuKey === JOBS_MENU_KEY) {
			dispatch(openProfileCompletionModal());
			return;
		}
		navigate(route);
	};

	const isActiveRoute = (route: string) => {
		if (route === "/") {
			return location.pathname === "/";
		}
		return location.pathname === route || location.pathname.startsWith(`${route}/`);
	};

	return (
		<aside className="fixed left-0 top-0 z-20 flex h-screen w-72 flex-col">
			<div className="sidebar-header flex shrink-0 items-center justify-center px-6 pb-6 pt-8">
				<OptimizedSvg
					src={sidebarLogo}
					alt="Skills Punjab"
					className="sidebar-logo w-full max-w-[14.5rem]"
					width="14.5rem"
					height="auto"
					cacheKey="auth-common-logo-sidebar"
				/>
			</div>

			<nav className="sidebar-nav flex-1 overflow-y-auto">
				<ul className="w-full">
					{menu.map(item => {
						const isActive = isActiveRoute(item.route);
						return (
							<li key={item.key}>
								<button
									type="button"
									onClick={() => handleNavigation(item.route, item.key)}
									aria-current={isActive ? "page" : undefined}
									className={`sidebar-nav-item flex w-full items-center gap-3 whitespace-nowrap transition focus:outline-none ${
										isActive
											? "sidebar-nav-item--active bg-sidebar-active text-white"
											: "text-white hover:bg-white/5"
									}`}>
									<span className="flex shrink-0 items-center [&_.MuiSvgIcon-root]:text-[1.5rem]">{item.icon}</span>
									<span className="flex-1 text-left">{item.label}</span>
								</button>
							</li>
						);
					})}
				</ul>
			</nav>
		</aside>
	);
};
